import { token } from '@/styled-system/tokens';
import type { TravelMode } from '@/src/lib/hooks/neighborhood';

export type { CommuteDestination, TravelMode } from '@/src/lib/hooks/neighborhood';

export type CommuteTime = {
  mode: TravelMode;
  duration: number;
  distance: number;
  durationText: string;
  distanceText: string;
};

export type POIOptionValue = string;
export type POICategory = POIOptionValue;
export type DefaultPOICategory =
  | 'default:restaurants'
  | 'default:cafes'
  | 'default:groceries'
  | 'default:shopping'
  | 'default:nightlife';

export type POIIconGroup =
  | 'restaurants'
  | 'cafes'
  | 'groceries'
  | 'shopping'
  | 'nightlife'
  | 'automotive'
  | 'business'
  | 'culture'
  | 'education'
  | 'entertainment'
  | 'facilities'
  | 'finance'
  | 'foodDrink'
  | 'geographicalAreas'
  | 'government'
  | 'healthWellness'
  | 'housing'
  | 'lodging'
  | 'naturalFeatures'
  | 'worship'
  | 'services'
  | 'sportsFitness'
  | 'transportation';

// A CSS color string produced by `token.var('colors.*')`. In prod builds Panda
// hashes token var names, so this is no longer the literal `var(--colors-…)` shape.
export type POIMarkerColor = string;

export type POICategoryLabelKey = 'poiRestaurants' | 'poiGroceries' | 'poiCafes' | 'poiShopping' | 'poiNightlife';

export type POIGroupLabelKey =
  | 'poiGroupFeatured'
  | 'poiGroupAutomotive'
  | 'poiGroupBusiness'
  | 'poiGroupCulture'
  | 'poiGroupEducation'
  | 'poiGroupEntertainment'
  | 'poiGroupFacilities'
  | 'poiGroupFinance'
  | 'poiGroupFoodDrink'
  | 'poiGroupGeographicalAreas'
  | 'poiGroupGovernment'
  | 'poiGroupHealthWellness'
  | 'poiGroupHousing'
  | 'poiGroupLodging'
  | 'poiGroupNaturalFeatures'
  | 'poiGroupPlacesOfWorship'
  | 'poiGroupServices'
  | 'poiGroupShopping'
  | 'poiGroupSports'
  | 'poiGroupTransportation';

export type POISearchConfig = {
  value: POIOptionValue;
  placesTypes: ReadonlyArray<string>;
  color: POIMarkerColor;
  iconGroup: POIIconGroup;
  label: string;
  labelKey?: POICategoryLabelKey;
  groupLabelKey: POIGroupLabelKey;
  isDefault?: boolean;
};

export type POIPlace = {
  placeId: string;
  name: string;
  lat: number;
  lng: number;
  category: POIOptionValue;
  address?: string;
  rating?: number;
  userRatingCount?: number;
  attributionHtml?: Array<string>;
};

type POIIconGroupStyle = {
  color: POIMarkerColor;
};

type GooglePlaceTypeGroup = {
  labelKey: POIGroupLabelKey;
  iconGroup: POIIconGroup;
  types: ReadonlyArray<string>;
};

const WORD_LABEL_OVERRIDES: Record<string, string> = {
  atm: 'ATM',
  ev: 'EV',
  rv: 'RV',
  us: 'US',
};

export const DEFAULT_POI_OPTION_VALUES: Array<DefaultPOICategory> = [
  'default:restaurants',
  'default:cafes',
  'default:groceries',
  'default:shopping',
  'default:nightlife',
];

export const POI_ICON_GROUP_STYLES: Record<POIIconGroup, POIIconGroupStyle> = {
  restaurants: { color: token.var('colors.teal.500') },
  cafes: { color: token.var('colors.purple.500') },
  groceries: { color: token.var('colors.red.400') },
  shopping: { color: token.var('colors.yellow.600') },
  nightlife: { color: token.var('colors.blue.500') },
  automotive: { color: token.var('colors.gray.600') },
  business: { color: token.var('colors.blue.600') },
  culture: { color: token.var('colors.purple.500') },
  education: { color: token.var('colors.blue.600') },
  entertainment: { color: token.var('colors.purple.400') },
  facilities: { color: token.var('colors.gray.600') },
  finance: { color: token.var('colors.green.600') },
  foodDrink: { color: token.var('colors.teal.500') },
  geographicalAreas: { color: token.var('colors.gray.700') },
  government: { color: token.var('colors.blue.500') },
  healthWellness: { color: token.var('colors.red.500') },
  housing: { color: token.var('colors.aqua.600') },
  lodging: { color: token.var('colors.orange.500') },
  naturalFeatures: { color: token.var('colors.green.500') },
  worship: { color: token.var('colors.purple.600') },
  services: { color: token.var('colors.orange.500') },
  sportsFitness: { color: token.var('colors.orange.500') },
  transportation: { color: token.var('colors.aqua.600') },
};

const FEATURED_POI_OPTIONS: ReadonlyArray<POISearchConfig> = [
  {
    value: 'default:restaurants',
    placesTypes: ['restaurant', 'meal_takeaway'],
    iconGroup: 'restaurants',
    color: POI_ICON_GROUP_STYLES.restaurants.color,
    label: 'Restaurants',
    labelKey: 'poiRestaurants',
    groupLabelKey: 'poiGroupFeatured',
    isDefault: true,
  },
  {
    value: 'default:cafes',
    placesTypes: ['cafe', 'coffee_shop'],
    iconGroup: 'cafes',
    color: POI_ICON_GROUP_STYLES.cafes.color,
    label: 'Cafes',
    labelKey: 'poiCafes',
    groupLabelKey: 'poiGroupFeatured',
    isDefault: true,
  },
  {
    value: 'default:groceries',
    placesTypes: ['grocery_store', 'supermarket', 'food_store', 'market', 'farmers_market'],
    iconGroup: 'groceries',
    color: POI_ICON_GROUP_STYLES.groceries.color,
    label: 'Groceries',
    labelKey: 'poiGroceries',
    groupLabelKey: 'poiGroupFeatured',
    isDefault: true,
  },
  {
    value: 'default:shopping',
    placesTypes: ['shopping_mall', 'store', 'department_store', 'clothing_store'],
    iconGroup: 'shopping',
    color: POI_ICON_GROUP_STYLES.shopping.color,
    label: 'Shopping',
    labelKey: 'poiShopping',
    groupLabelKey: 'poiGroupFeatured',
    isDefault: true,
  },
  {
    value: 'default:nightlife',
    placesTypes: ['night_club', 'bar', 'cocktail_bar', 'pub'],
    iconGroup: 'nightlife',
    color: POI_ICON_GROUP_STYLES.nightlife.color,
    label: 'Nightlife',
    labelKey: 'poiNightlife',
    groupLabelKey: 'poiGroupFeatured',
    isDefault: true,
  },
];

const GOOGLE_PLACE_TYPE_GROUPS: ReadonlyArray<GooglePlaceTypeGroup> = [
  {
    labelKey: 'poiGroupAutomotive',
    iconGroup: 'automotive',
    types: [
      'car_dealer',
      'car_rental',
      'car_repair',
      'car_wash',
      'ebike_charging_station',
      'electric_vehicle_charging_station',
      'gas_station',
      'parking',
      'parking_garage',
      'parking_lot',
      'rest_stop',
      'tire_shop',
      'truck_dealer',
    ],
  },
  {
    labelKey: 'poiGroupBusiness',
    iconGroup: 'business',
    types: [
      'business_center',
      'corporate_office',
      'coworking_space',
      'farm',
      'manufacturer',
      'ranch',
      'supplier',
      'television_studio',
    ],
  },
  {
    labelKey: 'poiGroupCulture',
    iconGroup: 'culture',
    types: [
      'art_gallery',
      'art_museum',
      'art_studio',
      'auditorium',
      'castle',
      'cultural_landmark',
      'fountain',
      'historical_place',
      'history_museum',
      'monument',
      'museum',
      'performing_arts_theater',
      'sculpture',
    ],
  },
  {
    labelKey: 'poiGroupEducation',
    iconGroup: 'education',
    types: [
      'academic_department',
      'educational_institution',
      'library',
      'preschool',
      'primary_school',
      'research_institute',
      'school',
      'secondary_school',
      'university',
    ],
  },
  {
    labelKey: 'poiGroupEntertainment',
    iconGroup: 'entertainment',
    types: [
      'adventure_sports_center',
      'amphitheatre',
      'amusement_center',
      'amusement_park',
      'aquarium',
      'banquet_hall',
      'barbecue_area',
      'botanical_garden',
      'bowling_alley',
      'casino',
      'childrens_camp',
      'city_park',
      'comedy_club',
      'community_center',
      'concert_hall',
      'convention_center',
      'cultural_center',
      'cycling_park',
      'dance_hall',
      'dog_park',
      'event_venue',
      'ferris_wheel',
      'garden',
      'go_karting_venue',
      'hiking_area',
      'historical_landmark',
      'indoor_playground',
      'internet_cafe',
      'karaoke',
      'live_music_venue',
      'marina',
      'miniature_golf_course',
      'movie_rental',
      'movie_theater',
      'national_park',
      'night_club',
      'observation_deck',
      'off_roading_area',
      'opera_house',
      'paintball_center',
      'park',
      'philharmonic_hall',
      'picnic_ground',
      'planetarium',
      'plaza',
      'roller_coaster',
      'skateboard_park',
      'state_park',
      'tourist_attraction',
      'video_arcade',
      'vineyard',
      'visitor_center',
      'water_park',
      'wedding_venue',
      'wildlife_park',
      'wildlife_refuge',
      'zoo',
    ],
  },
  {
    labelKey: 'poiGroupFacilities',
    iconGroup: 'facilities',
    types: ['public_bath', 'public_bathroom', 'stable'],
  },
  {
    labelKey: 'poiGroupFinance',
    iconGroup: 'finance',
    types: ['accounting', 'atm', 'bank'],
  },
  {
    labelKey: 'poiGroupFoodDrink',
    iconGroup: 'foodDrink',
    types: [
      'acai_shop',
      'afghani_restaurant',
      'african_restaurant',
      'american_restaurant',
      'argentinian_restaurant',
      'asian_fusion_restaurant',
      'asian_restaurant',
      'australian_restaurant',
      'austrian_restaurant',
      'bagel_shop',
      'bakery',
      'bangladeshi_restaurant',
      'bar',
      'bar_and_grill',
      'barbecue_restaurant',
      'basque_restaurant',
      'bavarian_restaurant',
      'beer_garden',
      'belgian_restaurant',
      'bistro',
      'brazilian_restaurant',
      'breakfast_restaurant',
      'brewery',
      'brewpub',
      'british_restaurant',
      'brunch_restaurant',
      'buffet_restaurant',
      'burmese_restaurant',
      'burrito_restaurant',
      'cafe',
      'cafeteria',
      'cajun_restaurant',
      'cake_shop',
      'californian_restaurant',
      'cambodian_restaurant',
      'candy_store',
      'cantonese_restaurant',
      'caribbean_restaurant',
      'cat_cafe',
      'chicken_restaurant',
      'chicken_wings_restaurant',
      'chilean_restaurant',
      'chinese_noodle_restaurant',
      'chinese_restaurant',
      'chocolate_factory',
      'chocolate_shop',
      'cocktail_bar',
      'coffee_roastery',
      'coffee_shop',
      'coffee_stand',
      'colombian_restaurant',
      'confectionery',
      'croatian_restaurant',
      'cuban_restaurant',
      'czech_restaurant',
      'danish_restaurant',
      'deli',
      'dessert_restaurant',
      'dessert_shop',
      'dim_sum_restaurant',
      'diner',
      'dog_cafe',
      'donut_shop',
      'dumpling_restaurant',
      'dutch_restaurant',
      'eastern_european_restaurant',
      'ethiopian_restaurant',
      'european_restaurant',
      'falafel_restaurant',
      'family_restaurant',
      'fast_food_restaurant',
      'filipino_restaurant',
      'fine_dining_restaurant',
      'fish_and_chips_restaurant',
      'fondue_restaurant',
      'food_court',
      'french_restaurant',
      'fusion_restaurant',
      'gastropub',
      'german_restaurant',
      'greek_restaurant',
      'gyro_restaurant',
      'halal_restaurant',
      'hamburger_restaurant',
      'hawaiian_restaurant',
      'hookah_bar',
      'hot_dog_restaurant',
      'hot_dog_stand',
      'hot_pot_restaurant',
      'hungarian_restaurant',
      'ice_cream_shop',
      'indian_restaurant',
      'indonesian_restaurant',
      'irish_pub',
      'irish_restaurant',
      'israeli_restaurant',
      'italian_restaurant',
      'japanese_curry_restaurant',
      'japanese_izakaya_restaurant',
      'japanese_restaurant',
      'juice_shop',
      'kebab_shop',
      'korean_barbecue_restaurant',
      'korean_restaurant',
      'latin_american_restaurant',
      'lebanese_restaurant',
      'lounge_bar',
      'malaysian_restaurant',
      'meal_delivery',
      'meal_takeaway',
      'mediterranean_restaurant',
      'mexican_restaurant',
      'middle_eastern_restaurant',
      'mongolian_barbecue_restaurant',
      'moroccan_restaurant',
      'noodle_shop',
      'north_indian_restaurant',
      'oyster_bar_restaurant',
      'pakistani_restaurant',
      'pastry_shop',
      'persian_restaurant',
      'peruvian_restaurant',
      'pizza_delivery',
      'pizza_restaurant',
      'polish_restaurant',
      'portuguese_restaurant',
      'pub',
      'ramen_restaurant',
      'restaurant',
      'romanian_restaurant',
      'russian_restaurant',
      'salad_shop',
      'sandwich_shop',
      'scandinavian_restaurant',
      'seafood_restaurant',
      'shawarma_restaurant',
      'snack_bar',
      'soul_food_restaurant',
      'soup_restaurant',
      'south_american_restaurant',
      'south_indian_restaurant',
      'southwestern_us_restaurant',
      'spanish_restaurant',
      'sports_bar',
      'sri_lankan_restaurant',
      'steak_house',
      'sushi_restaurant',
      'swiss_restaurant',
      'taco_restaurant',
      'taiwanese_restaurant',
      'tapas_restaurant',
      'tea_house',
      'tex_mex_restaurant',
      'thai_restaurant',
      'tibetan_restaurant',
      'tonkatsu_restaurant',
      'turkish_restaurant',
      'ukrainian_restaurant',
      'vegan_restaurant',
      'vegetarian_restaurant',
      'vietnamese_restaurant',
      'western_restaurant',
      'wine_bar',
      'winery',
      'yakiniku_restaurant',
      'yakitori_restaurant',
    ],
  },
  {
    labelKey: 'poiGroupGeographicalAreas',
    iconGroup: 'geographicalAreas',
    types: [
      'administrative_area_level_1',
      'administrative_area_level_2',
      'country',
      'locality',
      'postal_code',
      'school_district',
    ],
  },
  {
    labelKey: 'poiGroupGovernment',
    iconGroup: 'government',
    types: [
      'city_hall',
      'courthouse',
      'embassy',
      'fire_station',
      'government_office',
      'local_government_office',
      'neighborhood_police_station',
      'police',
      'post_office',
    ],
  },
  {
    labelKey: 'poiGroupHealthWellness',
    iconGroup: 'healthWellness',
    types: [
      'chiropractor',
      'dental_clinic',
      'dentist',
      'doctor',
      'drugstore',
      'general_hospital',
      'hospital',
      'massage',
      'massage_spa',
      'medical_center',
      'medical_clinic',
      'medical_lab',
      'pharmacy',
      'physiotherapist',
      'sauna',
      'skin_care_clinic',
      'spa',
      'tanning_studio',
      'wellness_center',
      'yoga_studio',
    ],
  },
  {
    labelKey: 'poiGroupHousing',
    iconGroup: 'housing',
    types: ['apartment_building', 'apartment_complex', 'condominium_complex', 'housing_complex'],
  },
  {
    labelKey: 'poiGroupLodging',
    iconGroup: 'lodging',
    types: [
      'bed_and_breakfast',
      'budget_japanese_inn',
      'campground',
      'camping_cabin',
      'cottage',
      'extended_stay_hotel',
      'farmstay',
      'guest_house',
      'hostel',
      'hotel',
      'inn',
      'japanese_inn',
      'lodging',
      'mobile_home_park',
      'motel',
      'private_guest_room',
      'resort_hotel',
      'rv_park',
    ],
  },
  {
    labelKey: 'poiGroupNaturalFeatures',
    iconGroup: 'naturalFeatures',
    types: ['beach', 'island', 'lake', 'mountain_peak', 'nature_preserve', 'river', 'scenic_spot', 'woods'],
  },
  {
    labelKey: 'poiGroupPlacesOfWorship',
    iconGroup: 'worship',
    types: ['buddhist_temple', 'church', 'hindu_temple', 'mosque', 'shinto_shrine', 'synagogue'],
  },
  {
    labelKey: 'poiGroupServices',
    iconGroup: 'services',
    types: [
      'aircraft_rental_service',
      'association_or_organization',
      'astrologer',
      'barber_shop',
      'beautician',
      'beauty_salon',
      'body_art_service',
      'catering_service',
      'cemetery',
      'chauffeur_service',
      'child_care_agency',
      'consultant',
      'courier_service',
      'electrician',
      'employment_agency',
      'florist',
      'food_delivery',
      'foot_care',
      'funeral_home',
      'hair_care',
      'hair_salon',
      'insurance_agency',
      'laundry',
      'lawyer',
      'locksmith',
      'makeup_artist',
      'marketing_consultant',
      'moving_company',
      'nail_salon',
      'non_profit_organization',
      'painter',
      'pet_boarding_service',
      'pet_care',
      'plumber',
      'psychic',
      'real_estate_agency',
      'roofing_contractor',
      'service',
      'shipping_service',
      'storage',
      'summer_camp_organizer',
      'tailor',
      'telecommunications_service_provider',
      'tour_agency',
      'tourist_information_center',
      'travel_agency',
      'veterinary_care',
    ],
  },
  {
    labelKey: 'poiGroupShopping',
    iconGroup: 'shopping',
    types: [
      'asian_grocery_store',
      'auto_parts_store',
      'bicycle_store',
      'book_store',
      'building_materials_store',
      'butcher_shop',
      'cell_phone_store',
      'clothing_store',
      'convenience_store',
      'cosmetics_store',
      'department_store',
      'discount_store',
      'discount_supermarket',
      'electronics_store',
      'farmers_market',
      'flea_market',
      'food_store',
      'furniture_store',
      'garden_center',
      'general_store',
      'gift_shop',
      'grocery_store',
      'hardware_store',
      'health_food_store',
      'home_goods_store',
      'home_improvement_store',
      'hypermarket',
      'jewelry_store',
      'liquor_store',
      'market',
      'pet_store',
      'shoe_store',
      'shopping_mall',
      'sporting_goods_store',
      'sportswear_store',
      'store',
      'supermarket',
      'tea_store',
      'thrift_store',
      'toy_store',
      'warehouse_store',
      'wholesaler',
      'womens_clothing_store',
    ],
  },
  {
    labelKey: 'poiGroupSports',
    iconGroup: 'sportsFitness',
    types: [
      'arena',
      'athletic_field',
      'fishing_charter',
      'fishing_pier',
      'fishing_pond',
      'fitness_center',
      'golf_course',
      'gym',
      'ice_skating_rink',
      'indoor_golf_course',
      'playground',
      'race_course',
      'ski_resort',
      'sports_activity_location',
      'sports_club',
      'sports_coaching',
      'sports_complex',
      'sports_school',
      'stadium',
      'swimming_pool',
      'tennis_court',
    ],
  },
  {
    labelKey: 'poiGroupTransportation',
    iconGroup: 'transportation',
    types: [
      'airport',
      'airstrip',
      'bike_sharing_station',
      'bridge',
      'bus_station',
      'bus_stop',
      'ferry_service',
      'ferry_terminal',
      'heliport',
      'international_airport',
      'light_rail_station',
      'park_and_ride',
      'subway_station',
      'taxi_service',
      'taxi_stand',
      'toll_station',
      'train_station',
      'train_ticket_office',
      'tram_stop',
      'transit_depot',
      'transit_station',
      'transit_stop',
      'transportation_service',
      'truck_stop',
    ],
  },
];

function formatPlaceTypeWord(word: string): string {
  const override = WORD_LABEL_OVERRIDES[word];
  if (override) return override;
  return `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
}

export function formatPlaceTypeLabel(placeType: string): string {
  return placeType.split('_').map(formatPlaceTypeWord).join(' ');
}

function createPlaceTypeOption(group: GooglePlaceTypeGroup, placeType: string): POISearchConfig {
  const { iconGroup, labelKey } = group;
  return {
    value: `type:${placeType}`,
    placesTypes: [placeType],
    iconGroup,
    color: POI_ICON_GROUP_STYLES[iconGroup].color,
    label: formatPlaceTypeLabel(placeType),
    groupLabelKey: labelKey,
  };
}

const GOOGLE_PLACE_TYPE_OPTION_GROUPS: ReadonlyArray<{
  labelKey: POIGroupLabelKey;
  options: ReadonlyArray<POISearchConfig>;
}> = GOOGLE_PLACE_TYPE_GROUPS.map((group) => ({
  labelKey: group.labelKey,
  options: group.types.map((placeType) => createPlaceTypeOption(group, placeType)),
}));

export const POI_OPTIONS: ReadonlyArray<POISearchConfig> = [
  ...FEATURED_POI_OPTIONS,
  ...GOOGLE_PLACE_TYPE_OPTION_GROUPS.flatMap((group) => group.options),
];

export const POI_OPTION_GROUPS: ReadonlyArray<{
  labelKey: POIGroupLabelKey;
  options: ReadonlyArray<POISearchConfig>;
}> = [{ labelKey: 'poiGroupFeatured', options: FEATURED_POI_OPTIONS }, ...GOOGLE_PLACE_TYPE_OPTION_GROUPS];

export const POI_SEARCH_CONFIGS: Record<string, POISearchConfig> = Object.fromEntries(
  POI_OPTIONS.map((option) => [option.value, option]),
);

export const DEFAULT_POI_CATEGORIES: Array<{
  key: DefaultPOICategory;
  labelKey: POICategoryLabelKey;
}> = FEATURED_POI_OPTIONS.map((option) => ({
  key: option.value as DefaultPOICategory,
  labelKey: option.labelKey!,
}));

export const PRIMARY_POI_CATEGORIES = DEFAULT_POI_CATEGORIES;

export function getPOISearchConfig(category: POIOptionValue): POISearchConfig {
  const config = POI_SEARCH_CONFIGS[category];
  if (!config) {
    throw new Error(`Unknown POI option: ${category}`);
  }
  return config;
}

export function getPOICategoryLabel(
  category: POIOptionValue,
  translateDefaultLabel: (labelKey: POICategoryLabelKey) => string,
): string {
  const config = getPOISearchConfig(category);
  return config.labelKey ? translateDefaultLabel(config.labelKey) : config.label;
}

export type TravelModeLabelKey = 'travelByCar' | 'travelOnFoot' | 'travelByTransit' | 'travelByBike';

export const TRAVEL_MODE_CONFIG: Array<{
  mode: TravelMode;
  labelKey: TravelModeLabelKey;
  icon: string;
}> = [
  { mode: 'DRIVING', labelKey: 'travelByCar', icon: 'car' },
  { mode: 'WALKING', labelKey: 'travelOnFoot', icon: 'walk' },
  { mode: 'TRANSIT', labelKey: 'travelByTransit', icon: 'transit' },
  { mode: 'BICYCLING', labelKey: 'travelByBike', icon: 'bike' },
];
